import type { Entry } from '@ozone/shared';
import { entryHeadline, formatDealSize } from '../compose';

interface EntryRowProps {
  entry: Entry;
  onToggleInclude: (entryId: string, next: boolean) => void;
}

/**
 * One MBR entry, rendered as a single dense paragraph. Mirrors the verbatim
 * AOZONE-31 composition (headline · deal size · narrative · action) but
 * with proper typography and hover affordance. <strong> is preserved for
 * customer name, deal size, and the Action: prefix — load-bearing for the
 * existing test selector.
 */
export function EntryRow({ entry, onToggleInclude }: EntryRowProps) {
  const dealSize = formatDealSize(entry);
  return (
    <div className="group flex items-start gap-3 rounded-md px-4 py-3 text-sm leading-relaxed text-slate-700 transition-colors hover:bg-slate-50">
      <input
        type="checkbox"
        checked={entry.includeInMbr ?? false}
        aria-label={`Include in MBR: ${entryHeadline(entry)}`}
        onChange={(e) => onToggleInclude(entry.entryId, e.target.checked)}
        className="mt-1 h-4 w-4 shrink-0 accent-slate-900"
      />
      <p className="flex-1">
        {entry.mbrPeriod && (
          <span
            data-testid="entry-mbr-period"
            className="mr-2 inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
          >
            {entry.mbrPeriod}
          </span>
        )}
        <strong className="font-semibold text-slate-900">
          {entryHeadline(entry)}
        </strong>
        {dealSize && (
          <>
            {' — '}
            <strong className="font-semibold text-slate-900">{dealSize}</strong>
          </>
        )}
        {entry.narrativeBody && (
          <>
            {' — '}
            {entry.narrativeBody}
          </>
        )}
        {entry.actionItem && (
          <>
            {' — '}
            <strong className="font-semibold text-slate-900">Action:</strong>
            {' ' + entry.actionItem}
          </>
        )}
      </p>
    </div>
  );
}
